import { useState, useCallback, useEffect } from 'react';
import { EEGTariff, Opex, Financing, ValidationsResponse } from '../../../../types';
import { tariffsApi } from '../../../../services/api/tariffs';
import { costsApi } from '../../../../services/api/costs';
import { financingsApi } from '../../../../services/api/financings';
import { useToast } from '../../../../contexts/ToastContext';

interface UseCalculationSlidersProps {
  projectId: string;
  canEdit: boolean;
  /** Callback nach erfolgreichem Speichern → löst Neuberechnung aus */
  onParameterSaved: () => Promise<void>;
  /** Callback für Validierungen aus API-Responses → PageWarningsContext */
  onValidationsReceived?: (validations: ValidationsResponse) => void;
}

interface UseCalculationSlidersResult {
  /** EEG-Tarif (Zuschlagswert) */
  eegTariff: EEGTariff | null;
  /** OPEX-Daten (Pachtkosten) */
  opex: Opex | null;
  /** Finanzierung (EK-Quote) */
  financing: Financing | null;
  /** Handler: Zuschlagswert ändern (aus EEG-Tarif) */
  handleZuschlagswertChange: (value: string) => Promise<void>;
  /** Handler: Pacht-Prozentsatz ändern (aus OPEX) */
  handlePachtProzentChange: (value: string) => Promise<void>;
  /** Handler: EK-Quote ändern (aus Financing) */
  handleEkQuoteChange: (value: string) => Promise<void>;
  /** Handler: LeaseTier-Prozentsatz ändern */
  handleLeaseTierPercentageChange: (tierId: string, percentage: number) => Promise<void>;
}

/** Wandelt String in Zahl für API-Updates */
const strToNum = (value: string): number | undefined => {
  if (!value || value.trim() === '') return undefined;
  const cleanValue = value.replace(/[^\d,.-]/g, '').replace(',', '.');
  const num = parseFloat(cleanValue);
  return isNaN(num) ? undefined : num;
};

/**
 * Hook für die 3 Haupt-Slider der Kalkulationsseite (Zuschlagswert, Pachtkosten, EK-Quote).
 *
 * Kapselt:
 * - Paralleles Laden der Slider-Daten beim Mount
 * - Optimistisches Update + API-Speicherung + Neuberechnung für jeden Handler
 *
 * Pattern: Save → onParameterSaved() → Error Handling
 */
export const useCalculationSliders = ({
  projectId,
  canEdit,
  onParameterSaved,
  onValidationsReceived,
}: UseCalculationSlidersProps): UseCalculationSlidersResult => {
  const [eegTariff, setEegTariff] = useState<EEGTariff | null>(null);
  const [opex, setOpex] = useState<Opex | null>(null);
  const [financing, setFinancing] = useState<Financing | null>(null);
  const { showSuccess, showError } = useToast();

  // Slider-Daten parallel laden
  useEffect(() => {
    if (!projectId) return;

    const loadExternalData = async () => {
      const [eegResult, opexResult, financingResult] = await Promise.allSettled([
        tariffsApi.getEEGTariff(projectId),
        costsApi.getOpex(projectId),
        financingsApi.getFinancing(projectId),
      ]);

      if (eegResult.status === 'fulfilled') setEegTariff(eegResult.value);
      if (opexResult.status === 'fulfilled') setOpex(opexResult.value);
      if (financingResult.status === 'fulfilled') setFinancing(financingResult.value);
    };

    loadExternalData();
  }, [projectId]);

  // Generischer Save+Recalculate-Wrapper
  const saveAndRecalculate = useCallback(async (
    label: string,
    saveFn: () => Promise<void>,
  ) => {
    try {
      await saveFn();
      showSuccess(`${label} gespeichert`);
      try {
        await onParameterSaved();
      } catch {
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : `Fehler beim Speichern (${label})`;
      showError(errorMsg);
    }
  }, [showSuccess, showError, onParameterSaved]);

  // Zuschlagswert (EEG-Tarif)
  const handleZuschlagswertChange = useCallback(async (value: string) => {
    const numValue = strToNum(value);
    setEegTariff(prev => prev ? { ...prev, zuschlagswert: numValue } : null);

    await saveAndRecalculate('Zuschlagswert', async () => {
      const updated = await tariffsApi.createOrUpdateEEGTariff(projectId, { zuschlagswert: numValue });
      setEegTariff(updated);
    });
  }, [projectId, saveAndRecalculate]);

  // Pacht-Prozentsatz (OPEX)
  const handlePachtProzentChange = useCallback(async (value: string) => {
    const numValue = strToNum(value);
    const pachtItem = opex?.items?.find(item => item.section === 'pachtkosten' && !item.is_editable);
    if (!pachtItem) return;

    setOpex(prev => {
      if (!prev) return null;
      const updatedItems = prev.items.map(item =>
        item.id === pachtItem.id ? { ...item, prozent_satz: numValue } : item
      );
      return { ...prev, items: updatedItems };
    });

    await saveAndRecalculate('Pachtkosten', async () => {
      await costsApi.updateOpexItem(projectId, pachtItem.id, {
        prozent_satz: numValue,
        modus: 'prozent',
      });
      const opexData = await costsApi.getOpex(projectId);
      setOpex(opexData);
    });
  }, [projectId, opex, saveAndRecalculate]);

  // EK-Quote (Financing)
  const handleEkQuoteChange = useCallback(async (value: string) => {
    const numValue = strToNum(value);
    setFinancing(prev => prev ? { ...prev, eigenkapitalquote: numValue } : null);

    await saveAndRecalculate('EK-Quote', async () => {
      const response = await financingsApi.updateFinancing(projectId, { eigenkapitalquote: numValue });
      setFinancing(response.data);
      onValidationsReceived?.(response.validations);
    });
  }, [projectId, saveAndRecalculate, onValidationsReceived]);

  // LeaseTier-Prozentsatz
  const handleLeaseTierPercentageChange = useCallback(async (tierId: string, percentage: number) => {
    setOpex(prev => {
      if (!prev) return null;
      const updatedItems = prev.items.map(item => {
        if (item.section === 'pachtkosten' && !item.is_editable && item.lease_tiers) {
          const updatedTiers = item.lease_tiers.map(tier =>
            tier.id === tierId ? { ...tier, percentage } : tier
          );
          return { ...item, lease_tiers: updatedTiers };
        }
        return item;
      });
      return { ...prev, items: updatedItems };
    });

    await saveAndRecalculate('Pachtstaffel', async () => {
      await costsApi.updateLeaseTier(projectId, tierId, { percentage });
      const opexData = await costsApi.getOpex(projectId);
      setOpex(opexData);
    });
  }, [projectId, saveAndRecalculate]);

  return {
    eegTariff,
    opex,
    financing,
    handleZuschlagswertChange,
    handlePachtProzentChange,
    handleEkQuoteChange,
    handleLeaseTierPercentageChange,
  };
};
